/**
 * PartnerAccountLayout — premium SaaS-style shell for the Partner portal,
 * mirroring the customer "My Account" UX. Sidebar on lg+, drawer on mobile.
 *
 * Sidebar sections (iter98):
 *   1.  Dashboard
 *   2.  My Profile
 *   3.  Referral Link & QR Code
 *   4.  Member Restaurants
 *   5.  Recurring Revenue
 *   6.  Stripe & Payouts
 *   7.  Marketing Support
 *   8.  Help Center
 *   9.  Contact Support
 *   10. Sign Out  (button)
 *
 * Auth: requires `nofee_partner_token` in localStorage. Otherwise redirects
 * to /partners/login.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  User,
  Link2,
  Store,
  DollarSign,
  CreditCard,
  Megaphone,
  HelpCircle,
  Mail,
  LogOut,
  Menu as MenuIcon,
  X,
  ChevronRight,
} from 'lucide-react';
import { NofeeNavLogo } from './NoFeeLogo';
import PartnerRecurringRevenueBanner from './PartnerRecurringRevenueBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PARTNER_NAV = [
  { key: 'dashboard',   path: '/partners/dashboard',            label: 'Dashboard',              Icon: LayoutDashboard },
  { key: 'profile',     path: '/partners/account/profile',      label: 'My Profile',             Icon: User },
  { key: 'referral',    path: '/partners/account/referral',     label: 'Referral Link & QR',     Icon: Link2 },
  { key: 'restaurants', path: '/partners/account/restaurants',  label: 'Member Restaurants',     Icon: Store },
  { key: 'commissions', path: '/partners/account/commissions',  label: 'Recurring Revenue',      Icon: DollarSign },
  { key: 'stripe',      path: '/partners/account/stripe',       label: 'Stripe & Payouts',       Icon: CreditCard },
  { key: 'marketing',   path: '/partners/account/marketing',    label: 'Marketing Support',      Icon: Megaphone },
  { key: 'help',        path: '/partners/help',                 label: 'Help Center',            Icon: HelpCircle },
  { key: 'contact',     path: '/partners/support/contact',      label: 'Contact Support',        Icon: Mail },
];

const PartnerAccountLayout = ({ children, currentKey, title, subtitle }) => {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_partner_token') : null;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [partner, setPartner] = useState(null);

  // Auth gate
  useEffect(() => {
    if (!token) {
      navigate('/partners/login', { replace: true });
    }
  }, [token, navigate]);

  // Fetch partner snapshot (sidebar header chip)
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/partners/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPartner(r.data?.partner || null))
      .catch(() => {});
  }, [token]);

  const onLogout = () => {
    try { localStorage.removeItem('nofee_partner_token'); } catch { /* ignore */ }
    navigate('/partners', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="partner-account-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/85"
              data-testid="partner-mobile-menu-btn"
              aria-label="Open menu"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
            <Link to="/" data-testid="partner-logo">
              <NofeeNavLogo height={36} />
            </Link>
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-gold-500/30 bg-gold-500/[0.06] text-[10px] uppercase tracking-[0.18em] text-gold-300 font-bold">
              Partners Portal
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onLogout}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 hover:border-rose-500/40 text-white/65 hover:text-rose-300 text-[12.5px] font-bold transition-all"
              data-testid="partner-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-7 grid lg:grid-cols-[260px_1fr] gap-6 lg:gap-8">
        {/* Sidebar — desktop only */}
        <aside className="hidden lg:block sticky top-[88px] self-start" data-testid="partner-sidebar">
          {partner && (
            <div className="mb-5 p-4 rounded-2xl border border-white/8 bg-white/[0.02]">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-gold-400 font-bold">
                Partner Account
              </div>
              <div className="mt-1 text-[14px] font-semibold text-white truncate">{partner.organization_name}</div>
              <div className="text-[12px] text-white/55 truncate">{partner.email}</div>
              <div className="mt-2 text-[11px] text-white/40 font-mono truncate">
                ID: {partner.partner_id}
              </div>
            </div>
          )}
          <nav className="space-y-1">
            {PARTNER_NAV.map((it) => {
              const active = currentKey === it.key;
              return (
                <Link
                  key={it.key}
                  to={it.path}
                  data-testid={`partner-nav-${it.key}`}
                  className={`group flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                    active
                      ? 'bg-gold-500/[0.08] border border-gold-500/40 text-gold-200'
                      : 'border border-transparent text-white/75 hover:text-gold-200 hover:bg-white/[0.03]'
                  }`}
                  aria-current={active ? 'page' : undefined}
                >
                  <span className="flex items-center gap-2.5">
                    <it.Icon className={`w-4 h-4 ${active ? 'text-gold-300' : 'text-white/55 group-hover:text-gold-300/85'}`} />
                    <span className="text-[14px] font-semibold">{it.label}</span>
                  </span>
                  <ChevronRight className={`w-3.5 h-3.5 ${active ? 'text-gold-300' : 'text-white/25'}`} />
                </Link>
              );
            })}
            <button
              type="button"
              onClick={onLogout}
              data-testid="partner-nav-signout"
              className="w-full mt-2 group flex items-center justify-between px-3 py-2.5 rounded-xl border border-transparent text-white/70 hover:text-rose-300 hover:bg-rose-500/[0.05] transition-all"
            >
              <span className="flex items-center gap-2.5">
                <LogOut className="w-4 h-4 text-white/55 group-hover:text-rose-300" />
                <span className="text-[14px] font-semibold">Sign Out</span>
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-white/25" />
            </button>
          </nav>
        </aside>

        {/* Main content */}
        <main data-testid="partner-main">
          {/* Global recurring-revenue banner — shown on every Partner page */}
          <PartnerRecurringRevenueBanner />

          {(title || subtitle) && (
            <div className="mb-6 sm:mb-7">
              {title && (
                <h1
                  data-testid="partner-page-title"
                  className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.03em] text-white"
                >
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="mt-2 text-[14px] sm:text-[15px] text-white/55 leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[80]" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className="absolute top-0 left-0 h-full w-[88%] max-w-[360px] bg-gradient-to-b from-zinc-950 via-black to-zinc-950 border-r border-gold-900/30 flex flex-col"
            data-testid="partner-mobile-drawer"
            style={{ animation: 'nofee-partner-drawer-slide 280ms cubic-bezier(0.22,1,0.36,1) both' }}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/[0.06]">
              <NofeeNavLogo height={36} />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/10 text-white/80"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {partner && (
              <div className="px-5 pt-4">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-gold-400 font-bold">
                  Partner Account
                </div>
                <div className="mt-1 text-[14px] font-semibold text-white truncate">{partner.organization_name}</div>
                <div className="text-[12px] text-white/55 truncate">{partner.email}</div>
              </div>
            )}
            <nav className="flex-1 overflow-y-auto px-3 pt-5 pb-4 space-y-1">
              {PARTNER_NAV.map((it) => {
                const active = currentKey === it.key;
                return (
                  <Link
                    key={it.key}
                    to={it.path}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`partner-mobile-nav-${it.key}`}
                    className={`flex items-center justify-between px-3 py-3.5 rounded-xl transition-all ${
                      active
                        ? 'bg-gold-500/[0.08] border border-gold-500/40 text-gold-200'
                        : 'border border-transparent text-white/80 hover:bg-white/[0.03]'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="flex items-center gap-3">
                      <it.Icon className={`w-4 h-4 ${active ? 'text-gold-300' : 'text-white/55'}`} />
                      <span className="text-[14px] font-semibold">{it.label}</span>
                    </span>
                  </Link>
                );
              })}
            </nav>
            <div className="px-5 pb-6 pt-3 border-t border-white/[0.06]">
              <button
                type="button"
                onClick={onLogout}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/12 hover:border-rose-500/40 text-white/70 hover:text-rose-300 text-[13px] font-bold transition-all whitespace-nowrap"
                data-testid="partner-mobile-logout-btn"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                <span className="truncate">Sign out</span>
              </button>
            </div>
          </aside>
          <style>{`
            @keyframes nofee-partner-drawer-slide {
              0% { transform: translateX(-100%); opacity: 0.6; }
              100% { transform: translateX(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default PartnerAccountLayout;
